/**
 * Auto-add gift and auto-apply discount rules.
 *
 * Gift thresholds and variant IDs: `cart-gift-helpers.js`. Edit discount config below.
 * @see docs/cart-auto-add.md for rules specification.
 */

import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { updateCartStore, getCartStore } from './cart-store.js';
import {
  GIFT_RULES,
  ALWAYS_GIFT_VARIANT_ID,
  getNonGiftSubtotal,
} from './cart-gift-helpers.js';

/* ── CONFIG: Edit these values ──────────────────────────────────────────── */

/** Product title patterns for discount eligibility (case-insensitive) */
const DISCOUNT_PRODUCT_PATTERNS = ['gummy', 'chi'];

/** Discount code to auto-apply when both patterns match */
const AUTO_DISCOUNT_CODE = 'CHI&GUMMY5';

/**
 * Master switch: when false, no auto gifts (tiered, always) and no auto discount code.
 * Set to `true` to re-enable all cart-auto-add behavior.
 */
const CART_AUTO_ADD_ENABLED = false;

/* ────────────────────────────────────────────────────────────────────────── */

const SOURCE = 'cart-auto-add';

/**
 * @param {number} subtotalCents - Cart subtotal excluding gifts
 * @returns {number | null} Variant ID of gift that should be in cart, or null
 */
function getGiftVariantForSubtotal(subtotalCents) {
  for (const rule of GIFT_RULES) {
    if (subtotalCents >= rule.thresholdCents) return rule.variantId;
  }
  return null;
}

/**
 * @param {object} cart
 * @returns {number}
 */
/**
 * @param {object} cart
 * @returns {boolean}
 */
function hasDiscountEligibleProducts(cart) {
  const items = cart?.items ?? [];
  const found = Object.fromEntries(
    DISCOUNT_PRODUCT_PATTERNS.map((p) => [p, false])
  );
  for (const item of items) {
    const title = (item.product_title ?? item.title ?? '').toLowerCase();
    const vendor = (item.vendor ?? '').toLowerCase();
    const productType = (item.product_type ?? '').toLowerCase();
    const sku = String(item.sku ?? '').toLowerCase();
    const haystack = `${title} ${vendor} ${productType} ${sku}`;
    for (const pattern of DISCOUNT_PRODUCT_PATTERNS) {
      if (haystack.includes(pattern.toLowerCase())) {
        found[pattern] = true;
      }
    }
  }
  return Object.values(found).every(Boolean);
}

/**
 * @param {string[]} existing
 * @param {boolean} shouldApply
 * @returns {string[]}
 */
function getDiscountCodesWithAuto(existing, shouldApply) {
  const withoutOurs = (existing || []).filter((c) => c !== AUTO_DISCOUNT_CODE);
  if (shouldApply) return [...withoutOurs, AUTO_DISCOUNT_CODE];
  return withoutOurs;
}

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

/**
 * @param {object} data
 */
function dispatchCartUpdate(data) {
  document.dispatchEvent(
    new CartUpdateEvent(data, SOURCE, {
      source: SOURCE,
      sections: data.sections,
      itemCount: data.item_count,
    })
  );
}

function getCartChangeUrl() {
  const base = Theme?.routes?.cart_change_url ?? '/cart/change';
  return base.endsWith('.js') ? base : base + '.js';
}

function getCartUpdateUrl() {
  const base = Theme?.routes?.cart_update_url ?? '/cart/update';
  return base.endsWith('.js') ? base : base + '.js';
}

/**
 * Build a map key for merging items by variant_id + properties.
 * @param {object} item
 * @returns {string}
 */
function getItemMergeKey(item) {
  const props = item.properties ?? {};
  const propsStr = JSON.stringify(props);
  return `${item.variant_id}:${propsStr}`;
}

/**
 * Merge cache cart with addedItems from cart/add.js response.
 * @param {object} cacheCart
 * @param {object[]} addedItems
 * @returns {object} Merged cart
 */
function mergeCartWithAddedItems(cacheCart, addedItems) {
  if (!Array.isArray(addedItems) || addedItems.length === 0) return cacheCart;

  const byKey = new Map();
  for (const item of cacheCart.items ?? []) {
    byKey.set(getItemMergeKey(item), { ...item });
  }
  for (const item of addedItems) {
    byKey.set(getItemMergeKey(item), { ...item });
  }
  const items = Array.from(byKey.values());
  const item_count = items.reduce((sum, i) => sum + (i.quantity || 0), 0);
  return {
    ...cacheCart,
    items,
    item_count,
    discount_codes: cacheCart.discount_codes ?? [],
  };
}

/**
 * Get cart from event (full cart) or merge cache + addedItems. Returns null if neither available.
 * @param {CustomEvent} event
 * @returns {object | null}
 */
function getCartFromCacheOrEvent(event) {
  const resource = event.detail?.resource;
  const data = event.detail?.data;

  if (resource?.items) return resource;
  if (data?.items) return data;

  const addedItems = data?.addedItems;
  const cache = getCartStore();
  if (cache.items.length > 0 && Array.isArray(addedItems) && addedItems.length > 0) {
    return mergeCartWithAddedItems(cache, addedItems);
  }
  return null;
}

/** @type {AbortController | null} */
let pendingOp = null;

/**
 * Apply gift and discount rules. Skips if event originated from this module.
 * @param {CustomEvent} event
 */
async function applyRules(event) {
  if (event.detail?.data?.source === SOURCE) return;
  if (event.detail?.data?.skipCartAutoAddRules) return;
  if (!CART_AUTO_ADD_ENABLED) return;

  if (pendingOp) pendingOp.abort();
  pendingOp = new AbortController();
  const signal = pendingOp.signal;

  try {
    let cart = getCartFromCacheOrEvent(event);
    if (!cart) {
      const res = await fetch('/cart.js', { signal });
      cart = await res.json();
      if (!cart?.items) return;
      updateCartStore(cart);
    } else {
      updateCartStore(cart);
    }
    if (!cart?.items) return;

    const sectionIds = getCartSectionIds();
    /** Section HTML for morph; empty if no cart-items on page — discount/cart/update still runs */
    const sections = sectionIds.join(',');
    const nonGiftSubtotal = getNonGiftSubtotal(cart);

    // Tiered threshold gifts: only when GIFT_RULES in cart-gift-helpers.js is non-empty.
    // When disabled ([]), always gift + CHI&GUMMY discount below still run.
    if (GIFT_RULES.length > 0) {
      const tieredGiftIds = new Set(GIFT_RULES.map((r) => String(r.variantId)));
      const desiredGiftVariantId = getGiftVariantForSubtotal(nonGiftSubtotal);
      const currentGiftItem = cart.items.find((i) => tieredGiftIds.has(String(i.variant_id)));

      if (desiredGiftVariantId) {
        const desiredIdStr = String(desiredGiftVariantId);
        const currentIdStr = currentGiftItem ? String(currentGiftItem.variant_id) : null;
        const needsUpgrade = currentGiftItem && currentIdStr !== desiredIdStr;
  
        if (needsUpgrade) {
          const res = await fetch(getCartUpdateUrl(), {
            ...fetchConfig('json', {
              body: JSON.stringify({
                updates: {
                  [currentGiftItem.variant_id]: 0,
                  [desiredGiftVariantId]: 1,
                },
                sections,
                sections_url: window.location.pathname,
              }),
            }),
            signal,
          });
          const data = await res.json();
          if (data.errors) return;
          updateCartStore(data);
          pendingOp = null;
          dispatchCartUpdate({ ...data, sections: data.sections });
          return applyRules(
            new CartUpdateEvent({ ...data, sections: data.sections }, SOURCE, {
              sections: data.sections,
              itemCount: data.item_count,
            })
          );
        }
  
        if (!currentGiftItem) {
          const addUrl = Theme?.routes?.cart_add_url ?? '/cart/add.js';
          const res = await fetch(addUrl, {
            ...fetchConfig('json', {
              body: JSON.stringify({
                items: [
                  {
                    id: desiredGiftVariantId,
                    quantity: 1,
                    properties: { _gift_pack: 'true' },
                  },
                ],
                sections,
                sections_url: window.location.pathname,
              }),
            }),
            signal,
          });
          const data = await res.json();
          if (data.status || data.description) return;
          const fullRes = await fetch('/cart.js').then((r) => r.json());
          updateCartStore(fullRes);
          pendingOp = null;
          dispatchCartUpdate({ ...fullRes, sections: data.sections });
          return applyRules(
            new CartUpdateEvent(
              { ...fullRes, sections: data.sections },
              SOURCE,
              { sections: data.sections, itemCount: fullRes.item_count }
            )
          );
        }
      } else if (currentGiftItem) {
        const giftKey = currentGiftItem.key;
        const res = await fetch(getCartChangeUrl(), {
          ...fetchConfig('json', {
            body: JSON.stringify({
              id: giftKey,
              quantity: 0,
              sections,
              sections_url: window.location.pathname,
            }),
          }),
          signal,
        });
        const data = await res.json();
        if (data.errors) return;
        updateCartStore(data);
        pendingOp = null;
        dispatchCartUpdate({ ...data, sections: data.sections });
        return applyRules(
          new CartUpdateEvent(data, SOURCE, {
            sections: data.sections,
            itemCount: data.item_count,
          })
        );
      }
    }

    // Always-add gift: when cart total > 0, ensure gift is present; when empty, remove
    const alwaysGiftStr = String(ALWAYS_GIFT_VARIANT_ID);
    const currentAlwaysGift = cart.items.find((i) => String(i.variant_id) === alwaysGiftStr);
    if (nonGiftSubtotal > 0 && !currentAlwaysGift) {
      const addUrl = Theme?.routes?.cart_add_url ?? '/cart/add.js';
      const res = await fetch(addUrl, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            items: [
              {
                id: ALWAYS_GIFT_VARIANT_ID,
                quantity: 1,
                properties: { _gift_always: 'true' },
              },
            ],
            sections,
            sections_url: window.location.pathname,
          }),
        }),
        signal,
      });
      const data = await res.json();
      if (data.status || data.description) {
        try {
          cart = await fetch('/cart.js', { signal }).then((r) => r.json());
          updateCartStore(cart);
        } catch (_) {}
      } else {
        const fullRes = await fetch('/cart.js').then((r) => r.json());
        updateCartStore(fullRes);
        pendingOp = null;
        dispatchCartUpdate({ ...fullRes, sections: data.sections });
        return applyRules(
          new CartUpdateEvent(
            { ...fullRes, sections: data.sections },
            SOURCE,
            { sections: data.sections, itemCount: fullRes.item_count }
          )
        );
      }
    }
    if (nonGiftSubtotal === 0 && currentAlwaysGift) {
      const res = await fetch(getCartChangeUrl(), {
        ...fetchConfig('json', {
          body: JSON.stringify({
            id: currentAlwaysGift.key,
            quantity: 0,
            sections,
            sections_url: window.location.pathname,
          }),
        }),
        signal,
      });
      const data = await res.json();
      if (data.errors) return;
      updateCartStore(data);
      pendingOp = null;
      dispatchCartUpdate({ ...data, sections: data.sections });
      return applyRules(
        new CartUpdateEvent(data, SOURCE, {
          sections: data.sections,
          itemCount: data.item_count,
        })
      );
    }

    if (!cart?.items) return;

    // Discount logic
    const shouldApplyDiscount = hasDiscountEligibleProducts(cart);
    const existingCodes = (cart.discount_codes ?? [])
      .map((d) => (typeof d === 'string' ? d : d?.code))
      .filter(Boolean);
    const targetCodes = getDiscountCodesWithAuto(existingCodes, shouldApplyDiscount);
    const codesStr = targetCodes.join(',');
    const currentStr = existingCodes.join(',');
    if (codesStr !== currentStr) {
      const res = await fetch(getCartUpdateUrl(), {
        ...fetchConfig('json', {
          body: JSON.stringify({
            discount: codesStr,
            sections,
            sections_url: window.location.pathname,
          }),
        }),
        signal,
      });
      const data = await res.json();
      // Match cart-add.js: only bail on explicit error status (200 = success if present)
      if (data.status && data.status !== 200) return;
      if (data.errors) return;
      updateCartStore(data);
      dispatchCartUpdate({ ...data, sections: data.sections });
    }
  } catch (err) {
    if (err.name !== 'AbortError') console.error('[cart-auto-add]', err);
  } finally {
    pendingOp = null;
  }
}

let applyTimeout = null;
function debouncedApply(event) {
  if (applyTimeout) clearTimeout(applyTimeout);
  applyTimeout = setTimeout(() => {
    applyTimeout = null;
    applyRules(event);
  }, 75);
}

document.addEventListener(ThemeEvents.cartUpdate, debouncedApply);
